import { useState, useEffect, useRef } from 'react';
import { Filter, ChevronDown, Check } from 'lucide-react';
import { STATUS_CFG } from '../../../helpers/Project-Report/utils/statusConfig';

const StatusFilterDropdown = ({ value, onChange, accent = '#3b82f6' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const options = Object.entries(STATUS_CFG).map(([key, val]) => ({
    value: key,
    label: val.label,
    color: val.color
  }));

  const handleToggle = (optValue) => {
    if (value.includes(optValue)) {
      onChange(value.filter(v => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  };

  const isAllSelected = value.length === options.length;

  const handleToggleAll = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange(options.map(o => o.value));
    }
  };

  let displayLabel = 'Filter Status';
  if (isAllSelected) {
    displayLabel = 'All Statuses';
  } else if (value.length === 1) {
    displayLabel = options.find(o => o.value === value[0])?.label || '1 Selected';
  } else if (value.length > 0) {
    displayLabel = `${value.length} Selected`;
  } else {
    displayLabel = 'None Selected';
  }

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          border: '1px solid #e2e8f0', borderRadius: '9px',
          padding: '0 12px', height: '34px', fontSize: '13px', color: '#334155',
          background: '#fff', cursor: 'pointer', outline: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '8px', minWidth: '130px', fontWeight: '500',
          borderColor: isOpen ? accent : '#e2e8f0',
          boxShadow: isOpen ? `0 0 0 3px ${accent}20` : '0 2px 4px rgba(0,0,0,0.02)',
          transition: 'all 0.2s',
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Filter size={14} color={accent} style={{ flexShrink: 0 }} />
          <span style={{ whiteSpace: 'nowrap' }}>{displayLabel}</span>
        </div>
        <ChevronDown size={14} color="#94a3b8" style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0,
            zIndex: 350, background: '#fff', borderRadius: '12px',
            border: '1.5px solid #e2e8f0', boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
            overflow: 'hidden', animation: 'dropFadeIn 0.15s ease',
            display: 'flex', flexDirection: 'column',
            width: '100%', minWidth: '180px'
          }}
        >
          <div style={{ padding: '4px' }}>
            {/* Toggle All Option */}
            <div
              onClick={handleToggleAll}
              style={{
                padding: '8px 10px', fontSize: '12px', color: '#334155',
                cursor: 'pointer', borderRadius: '6px',
                display: 'flex', alignItems: 'center', gap: '8px',
                background: isAllSelected ? `${accent}15` : 'transparent',
                fontWeight: isAllSelected ? '600' : '500',
                borderBottom: '1px solid #f1f5f9', marginBottom: '4px'
              }}
              onMouseEnter={(e) => { if (!isAllSelected) e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={(e) => { if (!isAllSelected) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ width: '14px', height: '14px', borderRadius: '4px', border: `1.5px solid ${isAllSelected ? accent : '#cbd5e1'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isAllSelected ? accent : '#fff' }}>
                {isAllSelected && <Check size={10} color="#fff" style={{ pointerEvents: 'none' }} />}
              </div>
              <span>Select All</span>
            </div>

            {options.map((opt, i) => {
              const isSelected = value.includes(opt.value);
              return (
                <div
                  key={i}
                  onClick={() => handleToggle(opt.value)}
                  style={{
                    padding: '6px 10px', fontSize: '12.5px', color: '#334155',
                    cursor: 'pointer', borderRadius: '6px',
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: isSelected ? `${accent}10` : 'transparent',
                    fontWeight: isSelected ? '500' : '400',
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ width: '14px', height: '14px', borderRadius: '4px', border: `1.5px solid ${isSelected ? accent : '#cbd5e1'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isSelected ? accent : '#fff' }}>
                    {isSelected && <Check size={10} color="#fff" style={{ pointerEvents: 'none' }} />}
                  </div>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: opt.color, flexShrink: 0 }} />
                  <span style={{ whiteSpace: 'nowrap' }}>{opt.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusFilterDropdown;
